import React, { useState, useEffect } from 'react';

// ── SettingsTab ──
// Sales team (read-only here — managed from main Settings page), Brokers (add/delete),
// and Commission structure (manager/closer/target/bonuses).
function SettingsTab({ team, brokers, deals = [], config, onAddBroker, onDeleteBroker, onSaveConfig, t, lang }) {
  const [cfg, setCfg] = useState(config);
  const [newBroker, setNewBroker] = useState({ name: '', type: 'company', default_pct: 2 });
  const [historyPrompt, setHistoryPrompt] = useState(null); // { mgrChanged, closerChanged, oldMgr, oldCloser, newMgr, newCloser }

  useEffect(() => {
    setCfg(config);
  }, [config]);

  // Count historical deals that would be touched if user opts in. Computed
  // client-side from the deals prop so the modal can show a live estimate
  // before the server confirms.
  const countAffected = (scope) => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    let mgrCount = 0;
    let closerCount = 0;
    let distinct = 0;
    deals.forEach((d) => {
      if (d.channel === 'reservation') return;
      const inScope = scope === 'all' || (d.deal_date && new Date(d.deal_date).getTime() >= yearStart);
      if (!inScope) return;
      const hitsMgr = Number(d.mgr_amt) > 0;
      const hitsCloser = d.channel !== 'broker' && Number(d.closer_amt) > 0;
      if (hitsMgr) mgrCount++;
      if (hitsCloser) closerCount++;
      if (hitsMgr || hitsCloser) distinct++;
    });
    return { distinct, mgrCount, closerCount };
  };

  const handleSaveClick = async () => {
    const mgrChanged = Number(cfg.amtMgr || 0) !== Number(config.amtMgr || 0);
    const closerChanged = Number(cfg.amtCloser || 0) !== Number(config.amtCloser || 0);
    // Only prompt when the two commission keys that get baked into deal rows
    // actually changed. Bonus / target edits never need retroactive sync.
    if (mgrChanged || closerChanged) {
      setHistoryPrompt({
        mgrChanged,
        closerChanged,
        oldMgr: Number(config.amtMgr || 0),
        oldCloser: Number(config.amtCloser || 0),
        newMgr: Number(cfg.amtMgr || 0),
        newCloser: Number(cfg.amtCloser || 0),
      });
    } else {
      await onSaveConfig({ ...cfg });
    }
  };

  const confirmHistory = async (scope) => {
    const payload = { ...cfg };
    if (scope !== 'none') payload._applyToHistory = scope;
    const res = await onSaveConfig(payload);
    setHistoryPrompt(null);
    if (scope !== 'none' && res && typeof res.updatedCount === 'number') {
      window.alert(
        lang === 'ar'
          ? `تم تحديث ${res.updatedCount} صفقة بالعمولات الجديدة.`
          : `Updated ${res.updatedCount} deals with the new commissions.`
      );
    }
  };

  const configItems = [
    { k: 'amtMgr', l: lang === 'ar' ? 'مدير المبيعات / صفقة' : 'Manager / Deal', suffix: 'SAR' },
    { k: 'amtCloser', l: lang === 'ar' ? 'منفّذ الصفقة / صفقة' : 'Closer / Deal', suffix: 'SAR' },
    { k: 'target', l: lang === 'ar' ? 'الهدف الشهري' : 'Monthly Target', suffix: lang === 'ar' ? 'صفقة' : 'deals' },
    { k: 'bonusImm', l: lang === 'ar' ? 'حافز فوري / صفقة' : 'Immediate Bonus / Deal', suffix: 'SAR' },
    { k: 'bonusDef', l: lang === 'ar' ? 'حافز مؤجل / صفقة' : 'Deferred Bonus / Deal', suffix: 'SAR' },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 14,
      }}
    >
      {/* Sales Team (read-only) */}
      <div className="card">
        <div className="card-header">
          <h2>{lang === 'ar' ? 'فريق المبيعات' : 'Sales Team'}</h2>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{team.length}</span>
        </div>
        <div style={{ padding: 12 }}>
          {team.length === 0 ? (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', padding: 12 }}>
              {lang === 'ar' ? 'لا يوجد أعضاء' : 'No team members'}
            </div>
          ) : (
            team.map((m, i) => (
              <div
                key={i}
                style={{
                  padding: '7px 0',
                  borderBottom: i < team.length - 1 ? '1px solid var(--border)' : 'none',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                }}
              >
                {m}
              </div>
            ))
          )}
          <div
            style={{
              fontSize: '0.65rem',
              color: 'var(--text-secondary)',
              marginTop: 10,
              paddingTop: 8,
              borderTop: '1px dashed var(--border)',
            }}
          >
            {lang === 'ar' ? 'يتم إدارة الفريق من صفحة الإعدادات الرئيسية' : 'Team is managed from the main Settings page'}
          </div>
        </div>
      </div>

      {/* Brokers */}
      <div className="card">
        <div className="card-header">
          <h2>{lang === 'ar' ? 'الوسطاء' : 'Brokers'}</h2>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{brokers.length}</span>
        </div>
        <div style={{ padding: 12 }}>
          {brokers.map((b) => (
            <div
              key={b.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px 0',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{b.name}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {b.type === 'company'
                    ? lang === 'ar'
                      ? 'شركة'
                      : 'Company'
                    : lang === 'ar'
                    ? 'مستقل'
                    : 'Individual'}{' '}
                  · {b.default_pct}%
                </div>
              </div>
              <button
                onClick={() => onDeleteBroker(b.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  padding: '2px 6px',
                }}
                title={lang === 'ar' ? 'حذف' : 'Delete'}
              >
                &#10005;
              </button>
            </div>
          ))}
          <div style={{ marginTop: 12, padding: '10px 0 0', borderTop: '1px dashed var(--border)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
              {lang === 'ar' ? 'إضافة وسيط' : 'Add Broker'}
            </div>
            <input
              value={newBroker.name}
              onChange={(e) => setNewBroker((p) => ({ ...p, name: e.target.value }))}
              placeholder={lang === 'ar' ? 'اسم الوسيط' : 'Broker name'}
              style={{
                width: '100%',
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                fontSize: '0.78rem',
                marginBottom: 6,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
              <select
                value={newBroker.type}
                onChange={(e) => setNewBroker((p) => ({ ...p, type: e.target.value }))}
                style={{
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  fontSize: '0.74rem',
                }}
              >
                <option value="company">{lang === 'ar' ? 'شركة' : 'Company'}</option>
                <option value="individual">{lang === 'ar' ? 'مستقل' : 'Individual'}</option>
              </select>
              <input
                type="number"
                value={newBroker.default_pct}
                onChange={(e) => setNewBroker((p) => ({ ...p, default_pct: Number(e.target.value) }))}
                step="0.1"
                placeholder="%"
                style={{
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  fontSize: '0.74rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (newBroker.name) {
                  onAddBroker(newBroker);
                  setNewBroker({ name: '', type: 'company', default_pct: 2 });
                }
              }}
              style={{ width: '100%' }}
              disabled={!newBroker.name}
            >
              {lang === 'ar' ? 'إضافة' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {/* Commission Config */}
      <div className="card">
        <div className="card-header">
          <h2>{lang === 'ar' ? 'هيكل العمولات' : 'Commission Structure'}</h2>
        </div>
        <div style={{ padding: 12 }}>
          {configItems.map((item) => (
            <div
              key={item.k}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid var(--border)',
                gap: 8,
              }}
            >
              <span style={{ flex: 1, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{item.l}</span>
              <input
                type="number"
                value={cfg[item.k] || 0}
                onChange={(e) => setCfg((p) => ({ ...p, [item.k]: Number(e.target.value) }))}
                style={{
                  width: 90,
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  fontSize: '0.78rem',
                  textAlign: 'center',
                  direction: 'ltr',
                }}
              />
              <span style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', minWidth: 32 }}>
                {item.suffix}
              </span>
            </div>
          ))}
          <button
            className="btn btn-primary"
            onClick={handleSaveClick}
            style={{ width: '100%', marginTop: 14, fontWeight: 700 }}
          >
            {t('save')}
          </button>
        </div>
      </div>

      {historyPrompt && (
        <HistoryPrompt
          info={historyPrompt}
          countAll={countAffected('all')}
          countYear={countAffected('year')}
          onPick={confirmHistory}
          onClose={() => setHistoryPrompt(null)}
          lang={lang}
        />
      )}
    </div>
  );
}

// Modal asking whether to retroactively apply the new commission amounts to
// historical deals. The user picks scope; the parent then sends the PUT with
// _applyToHistory and the server returns the affected count.
function HistoryPrompt({ info, countAll, countYear, onPick, onClose, lang }) {
  const isAr = lang === 'ar';
  const lines = [];
  if (info.mgrChanged) {
    lines.push({
      label: isAr ? 'مدير المبيعات / صفقة' : 'Manager / Deal',
      from: info.oldMgr,
      to: info.newMgr,
    });
  }
  if (info.closerChanged) {
    lines.push({
      label: isAr ? 'منفّذ الصفقة / صفقة' : 'Closer / Deal',
      from: info.oldCloser,
      to: info.newCloser,
    });
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 10, maxWidth: 520, width: '100%', padding: 20,
          boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
          {isAr ? 'تطبيق على الصفقات السابقة؟' : 'Apply to historical deals?'}
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
          {isAr
            ? 'تم تعديل عمولة الفريق. هل ترغب في تحديث الصفقات السابقة بالمبالغ الجديدة؟'
            : 'You changed the team commission. Apply the new amounts to existing deals too?'}
        </div>
        <div
          style={{
            background: 'var(--bg-primary)', border: '1px solid var(--border)',
            borderRadius: 8, padding: 10, marginBottom: 14, fontSize: '0.8rem',
          }}
        >
          {lines.map((ln, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{ln.label}</span>
              <span style={{ direction: 'ltr', fontWeight: 800 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{ln.from.toLocaleString()}</span>
                <span style={{ color: 'var(--text-secondary)', margin: '0 8px' }}>→</span>
                <span style={{ color: '#22c55e' }}>{ln.to.toLocaleString()}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', marginInlineStart: 4 }}>SAR</span>
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ScopeBtn
            primary
            title={isAr ? 'الصفقات المستقبلية فقط' : 'Future deals only'}
            sub={isAr ? 'لا تغيير على الصفقات السابقة (الوضع الافتراضي)' : 'No change to existing deals (default)'}
            onClick={() => onPick('none')}
          />
          <ScopeBtn
            title={isAr ? 'السنة الحالية فقط' : 'This year only'}
            sub={isAr
              ? `سيتم تحديث ${countYear.distinct} صفقة من ${new Date().getFullYear()}`
              : `Will update ${countYear.distinct} deals from ${new Date().getFullYear()}`}
            onClick={() => onPick('year')}
            disabled={countYear.distinct === 0}
          />
          <ScopeBtn
            danger
            title={isAr ? 'جميع الصفقات السابقة' : 'All historical deals'}
            sub={isAr
              ? `سيتم تحديث ${countAll.distinct} صفقة (لا يمكن التراجع)`
              : `Will update ${countAll.distinct} deals (cannot undo)`}
            onClick={() => onPick('all')}
            disabled={countAll.distinct === 0}
          />
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: 12, width: '100%', padding: '8px 12px',
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--text-secondary)', borderRadius: 6,
            fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
          }}
        >
          {isAr ? 'إلغاء' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}

function ScopeBtn({ title, sub, onClick, disabled, primary, danger }) {
  const accent = danger ? '#ef4444' : primary ? 'var(--accent)' : '#f59e0b';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: 'start',
        padding: '10px 12px',
        background: disabled ? 'var(--bg-primary)' : 'transparent',
        border: `1.5px solid ${disabled ? 'var(--border)' : accent}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{ fontSize: '0.86rem', fontWeight: 800, color: disabled ? 'var(--text-secondary)' : accent, marginBottom: 2 }}>
        {title}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
        {sub}
      </div>
    </button>
  );
}

export default SettingsTab;
